/**
 * Where a quoted span from a compile sits in the text as it stands now.
 *
 * A span's offsets point into the body that was compiled, and the author keeps typing. Nothing
 * errors when the two drift — the offsets stay valid numbers pointing at whatever now occupies
 * them — so trusting them blindly yields highlights sliced through unrelated words, or a
 * replacement spliced into a sentence nobody pointed at.
 */

export interface ILocatableSpan {
    start: number
    end: number
    /** The body substring the compiler resolved — always verbatim, never the model's paraphrase
     * (sealab stores `body.slice(start, end)`, not the quote the LLM copied). That is what makes
     * it usable as a checksum for the offsets, and as a way to find them again. */
    quote: string
}

/**
 * The span's range in `body`, or null if it can no longer be placed. Each fallback is less
 * certain than the last: unmoved offsets, then a constant shift when the whole compiled body
 * still sits verbatim inside the new one (which is what keeps highlights attached when someone
 * types a line above them), then a quote that occurs exactly once.
 *
 * Null means DROP, never guess. Two occurrences give no basis for choosing, and a highlight in
 * the wrong place is a claim about the author's text that isn't true.
 */
export function locateSpan(
    body: string,
    span: ILocatableSpan,
    compiledBody?: string,
): { start: number; end: number } | null {
    if (!span.quote) return null
    if (body.slice(span.start, span.end) === span.quote) return { start: span.start, end: span.end }

    if (compiledBody) {
        const shift = body.indexOf(compiledBody)
        if (shift !== -1) {
            const start = span.start + shift
            const end = span.end + shift
            if (body.slice(start, end) === compiledBody.slice(span.start, span.end)) return { start, end }
        }
    }

    const first = body.indexOf(span.quote)
    if (first === -1) return null
    if (body.indexOf(span.quote, first + 1) !== -1) return null

    return { start: first, end: first + span.quote.length }
}
